<template>
	<header-component type="threadRooms" @closePage="closePage" />
	<div class="thread-rooms">
		<base-button
			text="Начать новый чат"
			icon="newThread"
			class="thread-rooms-btn"
			variant="secondary"
			icon-position="left"
			@click="goToNewThread"
		/>
		<div v-for="g in groupedThreads" class="thread-rooms-group" :key="g.title">
			<p class="thread-rooms-date">{{ g.title }}</p>
			<thread-link
				v-for="thread in g.threads"
				:key="thread.id"
				:id="thread.id"
				:name="thread.name"
				:icon-url="thread.topic?.icon_url"
				@goToThread="goToThread"
				@threadLinkDelete="(id: number) => openDeleteRoomPopUp(id, g.sort)"
			/>
		</div>
	</div>
	<dialog-component
		title="Вы уверены что хотите удалить чат?"
		v-if="isDeleteRoomPopUpOpened"
		@close="closeDeleteRoomPopUp"
		@clickSecondBtn="closeDeleteRoomPopUp"
		@clickFirstBtn="deleteThread"
	/>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { onBeforeRouteLeave, useRouter } from 'vue-router'
import moment from 'moment'
import { useThreadStore } from '@/stores/thread'
import { useGenerationStore } from '@/stores/generation'
import { useComposerStore } from '@/stores/composer'
import HeaderComponent from '@/components/HeaderComponent.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import ThreadLink from '@/components/ThreadLink.vue'
import DialogComponent from '@/components/dialogs/DialogComponent.vue'
import type { ThreadLinkObject, GroupLinkObject, groupsLinkTitle } from '@/types'

const router = useRouter()
const threadStore = useThreadStore()
const generationStore = useGenerationStore()
const composerStore = useComposerStore()

const isDeleteRoomPopUpOpened = ref(false)
const groupedThreads = ref<GroupLinkObject[]>([])
const threadToDelete = ref<{ id: number; group: GroupLinkObject; index: number } | null>(null)
const prevThreadId = computed(() => threadStore.prevThreadId)
const deleteThreadArr = computed(() => threadStore.deleteThreadArr)

onMounted(async () => {
	const threadsList = await threadStore.getThreadsList()
	if (threadsList?.length) {
		groupedThreads.value = groupThreadsByDate(threadsList)
	}
})

function groupThreadsByDate(threads: ThreadLinkObject[]): GroupLinkObject[] {
	const groups: GroupLinkObject[] = []

	threads.forEach((thread) => {
		const groupTitle = getGroupTitle(thread.created_at)
		let group = groups.find((g) => g.title === groupTitle)

		if (!group) {
			group = { title: groupTitle, sort: getGroupSort(groupTitle), threads: [] }
			groups.push(group)
		}

		group.threads.push(thread)
	})

	return groups.sort((a, b) => a.sort - b.sort)
}

function getGroupTitle(date: string): groupsLinkTitle {
	if (moment(date).isSame(new Date(), 'day')) return 'Сегодня'
	if (isLessThan7DaysAgo(date)) return 'Неделя'
	return 'Ранее'
}

function getGroupSort(title: string): number {
	switch (title) {
		case 'Сегодня':
			return 1
		case 'Неделя':
			return 2
		default:
			return 3
	}
}

function isLessThan7DaysAgo(date: string): boolean {
	return moment().diff(moment(date), 'days') < 7
}

function openDeleteRoomPopUp(id: number, groupSort: number) {
	const group = groupedThreads.value.find((g) => g.sort === groupSort)
	if (group) {
		const index = group.threads.findIndex((t) => t.id === id)
		if (index !== -1) {
			threadToDelete.value = { id, group, index }
			isDeleteRoomPopUpOpened.value = true
		}
	}
}

function closeDeleteRoomPopUp() {
	isDeleteRoomPopUpOpened.value = false
	threadToDelete.value = null
}

async function deleteThread() {
	if (threadToDelete.value) {
		const { id, group, index } = threadToDelete.value
		const status = await threadStore.deleteThread(id)
		if (status === 204) {
			group.threads.splice(index, 1)
			threadStore.addToDeleteThreadArr(id)
			if (id === threadStore.thread.id) {
				threadStore.clearThread()
			}
			closeDeleteRoomPopUp()
		}
	}
}

async function goToThread(id: number) {
	const isSameGeneratingThread = generationStore.isBusy(id) && id === threadStore.prevThreadId

	if (!isSameGeneratingThread) {
		await threadStore.clearProccess()
		await threadStore.clearThread()
	}
	composerStore.clearReasoningMode()
	threadStore.clearSecretMode()
	router.push(`/thread/${id}`)
}

async function goToNewThread() {
	await threadStore.clearProccess()
	await threadStore.clearThread()
	threadStore.clearSecretMode()
	router.push('/thread')
}

function closePage() {
	if (prevThreadId.value && deleteThreadArr.value.includes(prevThreadId.value)) {
		router.push('/')
		return
	}
	window.history.back()
}

onBeforeRouteLeave((to, from) => {
	if (to.name === 'threadId' && deleteThreadArr.value.includes(Number(to.params.id))) {
		router.push('/')
		return
	}
})
</script>

<style lang="scss" scoped>
.thread-rooms {
	padding: 24px;
	width: 100%;
	&-date {
		color: var(--text-primary);
		font: var(--font-subtitle);
		margin-bottom: 12px;
	}
	&-btn {
		margin-bottom: 32px;
	}
	&-group:not(:last-child) {
		margin-bottom: 24px;
	}
}
</style>
